import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ClassroomEntryCard } from "@/components/home/ClassroomEntryCard";
import { HowToButton } from "@/components/home/HowToButton";
import { DemoLaunchers } from "@/components/home/ParticipantDemos";
import { schoolBatchText, useSchoolsPublic } from "@/components/home/SchoolRegister";
import { motion } from "framer-motion";
import { ArrowDown, ArrowRight, CalendarDays, Hash, School, Users } from "lucide-react";
import { resolveImg } from "@/lib/media";
import { SmileName } from "@/components/Smile";
import { DEFAULT_LAYOUT, LEFT_W } from "@/hooks/useLayout";
import { fade, PROGRAMS } from "./data";

const STEPS = [["1", "Register"], ["2", "Log in to Classroom"], ["3", "Open Video"]];
const ENTRY_BLOCKS = ["schedule", "timer", "tasks"]; // rendered by ClassroomEntryCard into the same slots

const BatchTicket = ({ content, suffix = "", vertical }) => {
  const schools = useSchoolsPublic();
  const nb = schools.enabled ? schools.next_batch : null;
  return (
  <div data-testid={`program-batch-ticket-1${suffix}`} className={`rounded-2xl border border-dashed border-[#1A2E26]/25 bg-[#F4F1EA] ${vertical ? "px-5 py-2 divide-y divide-[#1A2E26]/15" : "px-5 py-4 grid grid-cols-3 divide-x divide-[#1A2E26]/15"}`}>
    <div className={vertical ? "py-3 flex items-center justify-between gap-3" : "pr-4"}>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">{vertical && <Hash size={11} className="text-[#C86A51]" />}Next Batch</p>
      <p data-testid={`program-next-batch-1${suffix}`} className={`font-serif text-2xl ${vertical ? "" : "mt-1"}`}>{content?.batch_no || ""}</p>
    </div>
    <div className={vertical ? "py-3 flex items-center justify-between gap-3" : "px-4"}>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">{vertical && <CalendarDays size={11} className="text-[#C86A51]" />}Dates</p>
      <p data-testid={`program-batch-dates-1${suffix}`} className={`text-sm font-semibold leading-snug ${vertical ? "text-right" : "mt-1.5"}`}>{content?.batch_dates || ""}</p>
    </div>
    <div className={vertical ? "py-3 flex items-center justify-between gap-3" : "pl-4"}>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">{vertical && <Users size={11} className="text-[#C86A51]" />}Capacity</p>
      <p data-testid={`program-batch-capacity-1${suffix}`} className={`font-serif text-2xl text-[#B57E17] ${vertical ? "" : "mt-1"}`}>100</p>
    </div>
    {nb && (
      <div data-testid={`program-school-batch-1${suffix}`} className={`${vertical ? "py-3" : "col-span-3 mt-3 pt-3 border-t border-[#1A2E26]/15"} flex flex-wrap items-center justify-between gap-x-3 gap-y-1`}>
        <p className="text-[10px] uppercase tracking-widest text-[#5B7FA6] flex items-center gap-1.5"><School size={11} /> School Batch <span className="font-serif normal-case tracking-normal text-base text-[#1A2E26]">{nb.no}</span></p>
        <p className="text-xs font-semibold text-[#1A2E26]/80">{schoolBatchText(nb)}</p>
      </div>
    )}
  </div>
  );
};

const StepsHint = ({ content, suffix = "", vertical }) => {
  const steps = STEPS.filter(([k]) => (content?.classroom_home_steps || {})[k] !== false);
  return (
    <ol data-testid={`program-steps-hint${suffix}`} className={vertical ? "space-y-1.5" : "flex flex-wrap items-center gap-1.5 text-[11px] sm:text-xs text-[#1A2E26]/70"}>
      {vertical && <li className="text-[10px] uppercase tracking-[0.25em] text-[#C86A51] pb-1">How it works</li>}
      {steps.map(([k, t], idx) => (
        <li key={t} className={vertical ? "" : "flex items-center gap-1.5"}>
          <span className={`inline-flex items-center gap-2 rounded-full bg-[#1A2E26]/5 border border-[#1A2E26]/10 px-2.5 py-1 ${vertical ? "w-full text-xs text-[#1A2E26]/80" : ""}`}><span className="grid place-items-center w-4 h-4 rounded-full bg-[#1A2E26] text-white text-[9px] font-bold shrink-0">{k}</span>{t}</span>
          {idx < steps.length - 1 && (vertical ? <ArrowDown size={11} className="text-[#1A2E26]/40 ml-4 mt-1.5" /> : <ArrowRight size={11} className="text-[#1A2E26]/40" />)}
        </li>
      ))}
    </ol>
  );
};

const RegisterCta = ({ content, i, pname, regEnabled, isFull, fewLeft, onRegister, first }) => (
  <div>
    {(regEnabled && content?.registration_visible !== false) ? (
      !isFull ? (
        <button onClick={() => onRegister(pname)} data-testid={`program-register-${i}`} className={`register-cta self-start inline-flex items-center gap-2.5 rounded-full font-semibold text-white bg-gradient-to-r from-[#C86A51] to-[#E08A5B] hover:from-[#B85C45] hover:to-[#D47C4C] shadow-[0_10px_30px_rgba(200,106,81,0.35)] hover:shadow-[0_14px_36px_rgba(200,106,81,0.45)] hover:gap-3.5 transition-all duration-300 ${first ? "text-2xl sm:text-3xl px-8 py-3.5" : "text-base sm:text-lg px-6 py-2.5"}`}>
          Register <ArrowRight size={first ? 26 : 20} />
        </button>
      ) : (
        <span aria-disabled="true" data-testid={`program-register-${i}`} className="self-start inline-flex items-center gap-2 text-lg sm:text-xl font-semibold text-muted-foreground/40 cursor-not-allowed select-none">Register <span className="text-xs font-normal">(registrations full)</span></span>
      )
    ) : (
      <span aria-disabled="true" data-testid={`program-register-${i}`} className="self-start inline-flex items-center gap-2 text-lg sm:text-xl font-semibold text-muted-foreground/40 cursor-not-allowed select-none">Register <span className="text-xs font-normal">(coming soon)</span></span>
    )}
    {regEnabled && fewLeft && <p data-testid={`program-fewleft-${i}`} className="mt-2 text-xs font-medium text-[#B57E17]">Few seats left. Reserve your spot soon.</p>}
  </div>
);

const ProgramImage = ({ content, pname, i, regEnabled, isFull, corner }) => (
  <div className={`relative overflow-hidden ${corner ? "rounded-t-3xl sm:rounded-tr-none sm:rounded-l-3xl" : "rounded-2xl"}`}>
    <img src={resolveImg(content?.programs?.[0]?.image) || PROGRAMS[0].img} alt={pname} className="w-full h-48 sm:h-56 object-cover group-hover:scale-105 transition-transform duration-700" />
    {regEnabled && isFull && <span data-testid={`program-full-badge-${i}`} className="absolute top-3 left-3 inline-flex items-center rounded-full bg-destructive text-white px-3 py-1 text-xs font-semibold uppercase tracking-widest shadow">Full</span>}
  </div>
);

const useIsMobile = () => {
  const [m, setM] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches);
  useEffect(() => { const mq = window.matchMedia("(max-width: 639px)"); const on = (e) => setM(e.matches); mq.addEventListener("change", on); return () => mq.removeEventListener("change", on); }, []);
  return m;
};

/* Slot divs are laid out by the admin layout; each block is portaled into its slot (empty slots collapse). */
const Zone = ({ zone, items, setSlot, className, itemClass }) => (
  <div data-testid={`home-zone-${zone}`} data-zone={zone} className={className}>
    {items.filter((it) => it.visible).map((it, idx) => <div key={it.id} data-block={it.id} data-testid={`home-block-${it.id}`} ref={setSlot(it.id)} className={`empty:hidden ${itemClass ? itemClass(it.id, idx) : ""}`} />)}
  </div>
);

const useSlots = () => {
  const [slots, setSlots] = useState({});
  const cbs = useRef({});
  const setSlot = useCallback((id) => (cbs.current[id] ||= (el) => setSlots((s) => (s[id] === el ? s : { ...s, [id]: el }))), []);
  return [slots, setSlot];
};

const MainCard = ({ content, layout, p, i, regLimit, regCount, onRegister }) => {
  const mobile = useIsMobile();
  const [slots, setSlot] = useSlots();
  const pname = p.name;
  const regEnabled = p.register_enabled;
  const isFull = regLimit > 0 && regCount >= regLimit;
  const fewLeft = !isFull && regLimit > 0 && regCount >= regLimit * ((content?.few_left_pct ?? 80) / 100);
  const showSteps = !!content?.classroom_home_link;
  const home = layout || DEFAULT_LAYOUT.home;
  const zoneOf = (id) => ["left", "main", "inside"].find((z) => home[z].some((it) => it.id === id)) || "main";
  const inLeft = (id) => zoneOf(id) === "left";
  const render = (id) => {
    const vertical = !mobile && inLeft(id);
    const first = !mobile && home.left.filter((it) => it.visible)[0]?.id === id;
    switch (id) {
      case "image": return <ProgramImage content={content} pname={pname} i={i} regEnabled={regEnabled} isFull={isFull} corner={first || (mobile && home.mobile_order === "main_first")} />;
      case "howto": return <div className="space-y-2">{(content?.howto_look || "banner") === "button" ? <div><HowToButton content={content} /></div> : <HowToButton content={content} stacked={vertical} />}<DemoLaunchers placement="programs" size="sm" /></div>;
      case "steps": return showSteps ? <StepsHint content={content} vertical={vertical} suffix={vertical ? "" : "-m"} /> : null;
      case "title": return <div><h2 className="font-serif text-2xl md:text-3xl leading-tight mb-3" data-testid={`program-name-${i}`}><SmileName name={pname} size={26} testId="program-smile-name" /></h2><p className="text-muted-foreground leading-relaxed text-sm">{p.desc}</p></div>;
      case "register": return <RegisterCta content={content} i={i} pname={pname} regEnabled={regEnabled} isFull={isFull} fewLeft={fewLeft} onRegister={onRegister} first />;
      case "ticket": return <BatchTicket content={content} vertical={vertical} />;
      case "classroom": return showSteps ? <ClassroomEntryCard ui={content?.classroom_home_ui || "classic"} slots={slots} insideZone={<Zone zone="inside" items={home.inside} setSlot={setSlot} className="flex flex-col gap-4 mt-4" />} /> : null;
      default: return null;
    }
  };
  const leftItems = home.left.filter((it) => it.visible);
  const mainItems = home.main.filter((it) => it.visible);
  const img = leftItems.filter((it) => it.id === "image");
  const mobileItems = home.mobile_order === "left_first" ? [...leftItems, ...mainItems] : [...img, ...mainItems, ...leftItems.filter((it) => it.id !== "image")];
  const pad = (id, idx) => (id === "image" ? "" : `px-5 lg:px-4 ${idx === 0 ? "pt-5" : ""}`);
  return (
    <motion.div variants={fade} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} data-testid={`program-card-${i}`} data-split={home.split}
      className="group bg-white/70 backdrop-blur border border-white/60 rounded-3xl flex flex-col sm:flex-row shadow-[0_8px_40px_rgba(26,46,38,0.08)] hover:-translate-y-1 transition-transform duration-300">
      {mobile ? (
        <Zone zone="mobile" items={mobileItems} setSlot={setSlot} className="flex flex-col gap-5 pb-6" itemClass={pad} />
      ) : (
        <>
          <aside className={`${LEFT_W[home.split] || "sm:w-1/2"} ${leftItems.length ? "" : "hidden"}`} data-testid={`program-aside-${i}`}>
            <Zone zone="left" items={leftItems} setSlot={setSlot} className="flex flex-col gap-5 pb-5" itemClass={pad} />
          </aside>
          <div className="flex-1 min-w-0"><Zone zone="main" items={mainItems} setSlot={setSlot} className="p-7 flex flex-col gap-5" /></div>
        </>
      )}
      {Object.entries(slots).map(([id, el]) => (el && !ENTRY_BLOCKS.includes(id) ? createPortal(render(id), el, id) : null))}
    </motion.div>
  );
};

const SimpleCard = ({ content, p, i, regLimit, regCount, onRegister }) => {
  const isFull = regLimit > 0 && regCount >= regLimit;
  const fewLeft = !isFull && regLimit > 0 && regCount >= regLimit * ((content?.few_left_pct ?? 80) / 100);
  return (
    <motion.div variants={fade} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} data-testid={`program-card-${i}`}
      className="group bg-white/70 backdrop-blur border border-white/60 rounded-3xl flex flex-col sm:flex-row shadow-[0_8px_40px_rgba(26,46,38,0.08)] hover:-translate-y-1 transition-transform duration-300">
      <aside className="sm:w-2/5 lg:w-1/2" data-testid={`program-aside-${i}`}><ProgramImage content={content} pname={p.name} i={i} regEnabled={p.register_enabled} isFull={isFull} corner /></aside>
      <div className="p-7 flex flex-col flex-1 min-w-0 gap-4">
        <div><h2 className="font-serif text-2xl md:text-3xl leading-tight mb-3" data-testid={`program-name-${i}`}>{p.name}</h2><p className="text-muted-foreground leading-relaxed text-sm">{p.desc}</p></div>
        <RegisterCta content={content} i={i} pname={p.name} regEnabled={p.register_enabled} isFull={isFull} fewLeft={fewLeft} onRegister={onRegister} />
      </div>
    </motion.div>
  );
};

export const ProgramsSection = ({ content, regLimit, regCount, onRegister, layout, layoutReady = true }) => (
  <section id="programs" data-testid="programs-section" className="pt-20 md:pt-24 pb-24 scroll-mt-24">
    <motion.p variants={fade} custom={0} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-xs uppercase tracking-[0.3em] text-accent mb-10">Our Programs</motion.p>
    <div className="max-w-4xl lg:max-w-6xl space-y-8">
      {(content?.programs || []).map((p, i) => (i === 0
        ? (layoutReady && <MainCard key={i} content={content} layout={layout} p={p} i={i} regLimit={regLimit} regCount={regCount} onRegister={onRegister} />)
        : <SimpleCard key={i} content={content} p={p} i={i} regLimit={regLimit} regCount={regCount} onRegister={onRegister} />))}
    </div>
  </section>
);
